import { useState } from 'react';
import {
  BriefcaseIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { Modal } from '@components/common';
import type { McApplicantCardData } from '@components/templates/employer/applicant-list/flow-mc/types';
import { useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { useSwrScreeningItemByCandidate } from '@hooks/useSwrApi/endpoints/screening';
import { InterviewTranscriptModal } from '@components/features/screening/InterviewTranscriptModal/InterviewTranscriptModal';
import { InterviewAudioPlayer } from '@components/features/screening/InterviewAudioPlayer/InterviewAudioPlayer';

interface McPhoneApplicantPreviewModalProps {
  applicant?: McApplicantCardData;
  isOpen: boolean;
  onClose: () => void;
}

const normalizeScore = (score?: number | null) => {
  if (typeof score !== 'number' || !Number.isFinite(score)) return undefined;
  return `${Math.max(0, Math.min(100, Math.round(score)))}%`;
};

const DetailRow = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value?: string;
}) => {
  if (!value) return null;

  return (
    <div className="flex items-start gap-3 rounded-2xl border border-input/60 bg-white px-4 py-3">
      <div className="mt-0.5 shrink-0 text-ink-medium">{icon}</div>
      <div className="min-w-0">
        <div className="text-xs font-medium uppercase tracking-[0.12em] text-ink-light">{label}</div>
        <div className="mt-1 break-words text-sm font-medium text-ink">{value}</div>
      </div>
    </div>
  );
};

export const McPhoneApplicantPreviewModal = ({
  applicant,
  isOpen,
  onClose,
}: McPhoneApplicantPreviewModalProps) => {
  const { t } = useTranslation('common');
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);

  const handleClose = () => {
    setIsTranscriptOpen(false);
    onClose();
  };

  const { data: screeningItem } = useSwrScreeningItemByCandidate({
    matchingCandidateId: applicant?.mcIdentifier,
    enabled: isOpen,
  });

  const talentName = screeningItem?.name?.trim() || applicant?.privateInformation?.name?.trim();
  const leadExperienceTitle = screeningItem?.headline?.trim() || applicant?.leadExperience?.title?.trim();
  const leadExperienceCompany = applicant?.leadExperience?.company?.trim();
  const leadExperiencePeriod = applicant?.leadExperience?.period?.trim();
  const location = applicant?.location?.trim();
  const phone = screeningItem?.phone?.trim() || applicant?.privateInformation?.phone?.trim() || applicant?.mcPhoneIdentifierValue?.trim();
  const email = screeningItem?.email?.trim() || applicant?.privateInformation?.email?.trim();
  const normalizedScore = normalizeScore(screeningItem?.finalScore ?? applicant?.mcFinalScore);
  const isMcProcessing = applicant?.mcProcessingStatus === 'PROCESSING';
  const isMcInterviewProcessing = applicant?.mcProcessingStatus === 'INTERVIEW_PROCESSING';
  const displayMatchValue = isMcProcessing ? t('mc.applicant.upload_in_progress') : normalizedScore;
  const displaySummary = isMcProcessing
    ? t('mc.applicant.evaluation_in_progress')
    : (screeningItem?.interviewSummary?.trim() || applicant?.mcSummary?.trim());
  const modalTitle = talentName || leadExperienceTitle || phone || t('mc.applicant.phone_placeholder');
  const experienceMeta = [leadExperienceCompany, leadExperiencePeriod].filter(Boolean).join(' / ');

  const interviewStatus = screeningItem?.interviewStatus?.toLowerCase();
  const isInterviewCompleted = interviewStatus === 'completed' || interviewStatus === 'interview_completed';
  const hasInterviewData = isInterviewCompleted && screeningItem?.interviewId;

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="lg" backdropClickDisabled={isTranscriptOpen}>
        <Modal.Header onClose={handleClose}>{modalTitle}</Modal.Header>

        <Modal.Body className="space-y-5 p-6">
          {(talentName || leadExperienceTitle) && (
            <div className="rounded-3xl border border-input/60 bg-surface-50 p-5">
              {talentName && (
                <div className="text-xl font-semibold leading-tight text-ink">{talentName}</div>
              )}

              {leadExperienceTitle && (
                <div className="mt-2 text-base font-medium text-ink-medium">{leadExperienceTitle}</div>
              )}

              {experienceMeta && (
                <div className="mt-1 text-sm text-ink-light">{experienceMeta}</div>
              )}
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <DetailRow
              icon={<PhoneIcon className="size-5" />}
              label={t('global.label.phone')}
              value={phone}
            />
            <DetailRow
              icon={<EnvelopeIcon className="size-5" />}
              label={t('global.label.email')}
              value={email}
            />
            <DetailRow
              icon={<MapPinIcon className="size-5" />}
              label={t('global.location.label')}
              value={location}
            />
            <DetailRow
              icon={<BriefcaseIcon className="size-5" />}
              label={t('cv.section.label.professional_experience')}
              value={leadExperienceTitle}
            />
          </div>

          {(displayMatchValue || displaySummary || isMcInterviewProcessing) && (
            <div className="rounded-3xl border border-input/60 bg-surface-50 p-5">
              {displayMatchValue && (
                <div className="text-sm">
                  <span className="font-medium text-ink">Match:</span>
                  {' '}
                  <span className="font-semibold text-ink-medium">{displayMatchValue}</span>
                </div>
              )}

              {isMcInterviewProcessing && (
                <div className="mt-1 text-xs italic text-ink-light">
                  {t('mc.applicant.partial_match_increasing')}
                </div>
              )}

              {displaySummary && (
                <div className="mt-3 whitespace-pre-wrap text-sm text-ink-medium">{displaySummary}</div>
              )}
            </div>
          )}

          {hasInterviewData && (
            <div className="space-y-3 rounded-3xl border border-input/60 bg-surface-50 p-5">
              <button
                type="button"
                onClick={() => setIsTranscriptOpen(true)}
                className="flex items-center gap-2 text-sm font-medium text-emerald-600 transition-colors hover:text-emerald-700"
              >
                <DocumentTextIcon className="size-4" />
                {t('screening.view.transcript')}
              </button>

              <InterviewAudioPlayer interviewId={screeningItem.interviewId!} />
            </div>
          )}
        </Modal.Body>
      </Modal>

      {hasInterviewData && (
        <InterviewTranscriptModal
          isOpen={isTranscriptOpen}
          onClose={() => setIsTranscriptOpen(false)}
          interviewId={screeningItem.interviewId!}
          candidateName={talentName}
        />
      )}
    </>
  );
};
